import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Container, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Navigate, useLocation } from "react-router-dom";
import moment from "moment";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchWorkdaySyncReport, WorkdaySyncReportDetail } from "../../redux/WorkdaySyncReportReducer/workdaySyncReportSlice";
import { getDefaultReportStartDate, getDefaultReportEndDate } from '../../utils/reportDateDefaults';
import ReportDateRangeFilter from '../../components/common/ReportDateRangeFilter';
import ResponsiveCardList from '../../components/common/ResponsiveCardList';
import useIsMobile from '../../hooks/useIsMobile';
import { reportDataGridSx } from '../../styles/reportDataGridSx';

const columns: GridColDef[] = [
    { field: "employeeId", headerName: "Worker ID", flex: 1, minWidth: 140 },
    { field: "employeeNm", headerName: "Worker", flex: 1, minWidth: 140 },
    {
        field: "requestDt",
        headerName: "Shift Date",
        flex: 1,
        minWidth: 120,
        valueFormatter: (value: string | null) => value ? moment(value).format("MM/DD/YYYY") : "-",
    },
    { field: "requestType", headerName: "Shift Type", flex: 1, minWidth: 110 },
    { field: "requestedHours", headerName: "Hours", flex: 1, minWidth: 120 },
    {
        field: "syncedAt",
        headerName: "Synced Timestamp",
        flex: 1,
        minWidth: 180,
        valueFormatter: (value: string | null) => value ? moment(value).format("MM/DD/YYYY h:mm A") : "-",
    },
    { field: "requiredSkill", headerName: "Skill", flex: 1, minWidth: 120 },
    { field: "requiredTeam", headerName: "Team", flex: 1, minWidth: 120 },
    {
        field: "requiredNotes",
        headerName: "Notes",
        flex: 1,
        minWidth: 120,
        valueGetter: (_value, row: WorkdaySyncReportDetail) => row.requiredNotes?.trim() || "—",
    },
    { field: "timeOffCd", headerName: "Time Off Code", flex: 1, minWidth: 120 },
];

const WorkdaySyncReport: React.FC = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { user: currentUser } = useAppSelector((state) => state.currentUser);
    const { workdaySyncData, loading, error } = useAppSelector((state) => state.workdaySyncReport);
    const [fromDate, setFromDate] = useState<string>(getDefaultReportStartDate());
    const [toDate, setToDate] = useState<string>(getDefaultReportEndDate());

    useEffect(() => {
        if (currentUser?.siteCd) {
            dispatch(fetchWorkdaySyncReport({
                siteCd: currentUser.siteCd,
                startDate: fromDate || undefined,
                endDate: toDate || undefined,
            }));
        }
    }, [dispatch, currentUser, fromDate, toDate]);

    useEffect(() => {
        if (error) {
            toast.error(`Failed to load Workday sync report: ${error}`);
        }
    }, [error]);

    const rows = useMemo(() => workdaySyncData, [workdaySyncData]);
    const isMobile = useIsMobile('md');

    if (!location.state?.fromWorkdaySyncCard) {
        return <Navigate to="/reports" replace />;
    }

    const renderMobileList = () => (
        <ResponsiveCardList
            rows={rows}
            getRowId={(row) => `${row.requestId}-${row.requestSeqId}-${row.wid ?? ''}-${row.syncedAt ?? ''}`}
            primary={(row) => row.employeeNm || row.employeeId || '—'}
            secondary={(row) => (
                <Typography variant="caption" color="text.secondary">
                    {row.requestDt ? moment(row.requestDt).format('MM/DD/YYYY') : '—'}
                </Typography>
            )}
            fields={[
                { label: 'Worker ID', field: 'employeeId' },
                { label: 'Shift Type', field: 'requestType' },
                { label: 'Hours', field: 'requestedHours' },
                {
                    label: 'Synced',
                    render: (row) => row.syncedAt ? moment(row.syncedAt).format('MM/DD/YYYY h:mm A') : '—',
                },
                { label: 'Skill', field: 'requiredSkill' },
                { label: 'Team', field: 'requiredTeam' },
                { label: 'Time Off Code', field: 'timeOffCd' },
                { label: 'Notes', render: (row) => row.requiredNotes?.trim() || '—' },
            ]}
        />
    );

    return (
        <Container
            maxWidth={false}
            sx={{
                py: { xs: 2.5, md: 3, lg: 3.5, xl: 4 },
                px: { xs: 1, sm: 2, md: 3.5, lg: 4, xl: 5 },
            }}
        >
            <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                    <Typography variant="h3" component="h1" className="page-title">
                        Workday Sync
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        The following table contains all of the workday synced shifts within the specified date range
                    </Typography>
                </Stack>


                <Stack spacing={1.5} sx={{ p: 2 }}>
                    <ReportDateRangeFilter
                        fromDate={fromDate}
                        toDate={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                    />
                    <Box sx={{ width: "100%", flex: 1, minHeight: 0, overflow: "hidden" }}>
                        {loading ? (
                            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: "200px" }}>
                                <CircularProgress />
                                <Typography variant="body1">Loading Workday sync data...</Typography>
                            </Stack>
                        ) : isMobile ? (
                            renderMobileList()
                        ) : (
                            <DataGrid
                                rows={rows}
                                getRowId={(row: WorkdaySyncReportDetail) => `${row.requestId}-${row.requestSeqId}-${row.wid ?? ''}-${row.syncedAt ?? ''}`}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { pageSize: 10 },
                                    },
                                }}
                                pageSizeOptions={[5, 10, 25, 50]}
                                disableRowSelectionOnClick
                                sx={{
                                    ...reportDataGridSx,
                                    height: "calc(100vh - 330px)",
                                    minHeight: 320,
                                }}
                            />
                        )}
                    </Box>
                </Stack>
            </Stack>
        </Container>
    );
};

export default WorkdaySyncReport;
